import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api, { apiErr, fmtTime } from "../../lib/api";
import { Card, Alert } from "../ui-bits";
import { ClipCard, toClip } from "./clip-utils";
import PexelsBrowser from "./PexelsBrowser";
import { toast } from "sonner";
import { playDoneSound } from "../../lib/sound";
import { useUploads } from "../../context/UploadContext";
import { useQuota } from "../../context/QuotaContext";
import { FolderOpen, Upload, Loader2, Plus, Trash2, Search, X, ArrowLeft, Folder, Film, Wand2, Layers, ChevronDown } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../ui/tooltip";

export default function MyFolder({ projectId, keywords = [], characters = [], library, loading, onReload, onUse, targetLabel, emptyScene = false, autoOpenSearch = false, replaceOnly = false, initialMedia, initialSource }) {
  const { t } = useTranslation();
  const { quota } = useQuota() || {};
  const fileRef = useRef();
  const [showPexels, setShowPexels] = useState(autoOpenSearch);
  const [openFolderId, setOpenFolderId] = useState(null);
  const [atRoot, setAtRoot] = useState(false);
  const [activeVid, setActiveVid] = useState(projectId);

  const { startUploads, registerReload, hasActive, activePct, activeBatch, startCut, isCutting, getCut } = useUploads();
  useEffect(() => registerReload && registerReload(projectId, onReload), [projectId, onReload, registerReload]);

  // Reanuda el sondeo de cortes que siguen "cortando" (p.ej. al volver a abrir la pestaña).
  useEffect(() => {
    (library?.videos || []).forEach((v) => {
      if (v.cut_status === "cortando" && startCut && !isCutting(v.id)) {
        startCut(projectId, v.id, v.title, { resume: true });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [library]);

  const uploading = hasActive ? hasActive(projectId) : false;
  const uploadPct = activePct ? activePct(projectId) : null;
  const uploadBatch = activeBatch ? activeBatch(projectId) : null;

  const handleUpload = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    startUploads && startUploads(projectId, files);
  };

  const cutVideo = (fileId, name) => startCut && startCut(projectId, fileId, name);

  const deleteItem = async (id, label) => {
    if (!window.confirm(t("mf.removeConfirm", { label }))) return;
    try {
      await api.delete(`/projects/${projectId}/broll/library/${id}`);
      toast.success(t("mf.removed"));
      onReload && onReload();
    } catch (e) { toast.error(apiErr(e)); }
  };

  // Vacía de un golpe TODA la carpeta de un vídeo (botón en cada VideoFolderTile de la raíz) —
  // a diferencia de deleteItem (un archivo o una sub-carpeta), esto usa el project_id de la
  // propia carpeta (vf.project_id), no el projectId del vídeo que se está editando ahora: así
  // funciona igual para la carpeta actual que para la de cualquier otro vídeo. Borrado real,
  // sin papelera — de ahí el conteo de archivos en la confirmación.
  const deleteWholeFolder = async (vf) => {
    const count = (vf.videos?.length || 0) + (vf.folders || []).reduce((a, f) => a + (f.parts?.length || 0), 0);
    if (!count) return;
    if (!window.confirm(t("mf.removeFolderConfirm", { label: vf.name, count }))) return;
    try {
      await api.delete(`/projects/${vf.project_id}/broll/library`);
      toast.success(t("mf.folderRemoved"));
      onReload && onReload();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const lib = library || {};
  const isCurrent = activeVid === projectId;
  const activeLib = isCurrent
    ? { videos: lib.videos || [], folders: lib.folders || [], name: lib.current_name || t("mf.thisVideo") }
    : (lib.others || []).find((o) => o.project_id === activeVid) || { videos: [], folders: [], name: t("mf.videoWord") };
  const videoFolders = [
    { project_id: projectId, name: lib.current_name || t("mf.thisVideo"), videos: lib.videos || [], folders: lib.folders || [], is_current: true },
    ...(lib.others || []),
  ];
  const openFolder = activeLib.folders?.find((f) => f.id === openFolderId);
  const totallyEmpty = videoFolders.every((vf) => !vf.videos.length && !vf.folders.length);

  const goRoot = () => { setAtRoot(true); setOpenFolderId(null); };
  const openVideo = (vpid) => { setActiveVid(vpid); setAtRoot(false); setOpenFolderId(null); };

  return (
    <Card className="p-6 flex flex-col">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="bg-amber-100 text-amber-600 rounded-lg p-2"><FolderOpen size={24} strokeWidth={2.5} /></div>
          <div>
            <h3 className="text-xl font-bold font-head text-slate-900">{t("mf.title")}</h3>
            <p className="text-base text-slate-600">{t("mf.subtitle")}</p>
          </div>
        </div>
        {isCurrent && !atRoot && (
          <div className="flex flex-col sm:flex-row gap-2 sm:shrink-0">
            <input ref={fileRef} type="file" accept="image/*,video/*" multiple onChange={handleUpload} className="hidden" data-testid="broll-upload-input" />
            <button data-testid="open-pexels-button" onClick={() => setShowPexels((v) => !v)} aria-expanded={showPexels} aria-controls="pexels-inline-panel"
              className={`min-h-[48px] px-5 text-base font-bold rounded-xl transition-colors flex items-center justify-center gap-2 whitespace-nowrap ${showPexels ? "bg-blue-800 text-white" : "bg-blue-600 text-white hover:bg-blue-700"} ${emptyScene && !showPexels ? "animate-pulse-glow" : ""}`}>
              <Search size={20} /> {t("mf.search")} <ChevronDown size={18} className={`transition-transform ${showPexels ? "rotate-180" : ""}`} />
            </button>
            <TooltipProvider delayDuration={120}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button data-testid="broll-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading} className={`min-h-[48px] px-5 text-base font-bold rounded-xl bg-slate-900 text-white hover:bg-slate-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60 whitespace-nowrap ${emptyScene && !uploading ? "animate-pulse-glow" : ""}`}>
                    {uploading ? <Loader2 className="animate-spin" size={20} /> : <Upload size={20} />} {uploading ? (uploadBatch ? t("mf.uploadingBatch", { current: uploadBatch.current, total: uploadBatch.total }) : uploadPct != null ? t("mf.uploadingN", { progress: `${uploadPct}%` }) : t("mf.uploading")) : t("mf.uploadImgVid")}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[270px] bg-slate-900 text-white text-sm font-medium leading-snug px-3 py-2 rounded-xl shadow-2xl border border-white/10">
                  {t("mf.maxUploadHint", { limit: quota?.max_upload_mb || 250 })}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      </div>

      {showPexels && isCurrent && !atRoot && (
        <div id="pexels-inline-panel" data-testid="pexels-inline-panel" className="mb-4 bg-slate-50 border-2 border-blue-100 rounded-xl p-4 sm:p-5 animate-in fade-in slide-in-from-top-1 duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 text-blue-700 rounded-lg p-2"><Search size={22} strokeWidth={2.5} /></div>
              <div>
                <h3 className="text-lg font-bold font-head text-slate-900">{t("mf.pexelsTitle")}</h3>
                <p className="text-sm text-slate-600">{t("mf.pexelsSub")}</p>
              </div>
            </div>
            <button data-testid="close-pexels-button" onClick={() => setShowPexels(false)} aria-label={t("mf.done")} className="min-h-[40px] min-w-[40px] rounded-lg text-slate-500 hover:bg-slate-200 flex items-center justify-center"><X size={22} /></button>
          </div>
          <PexelsBrowser projectId={projectId} keywords={keywords} characters={characters} onSaved={onReload}
            onUse={isCurrent ? onUse : undefined} replaceOnly={replaceOnly}
            initialMedia={initialMedia} initialSource={initialSource} />
        </div>
      )}

      {targetLabel && (
        <div data-testid="active-scene-banner" className="mb-4 flex items-center gap-2 bg-blue-50 border-2 border-blue-200 rounded-xl px-4 py-3 text-base font-semibold text-blue-800">
          <span className="text-blue-500">➜</span> {t("mf.addingTo")} <span className="font-bold">{targetLabel}</span>
        </div>
      )}

      {/* Breadcrumb de carpetas de video */}
      <div className="flex items-center gap-2 text-base mb-4 flex-wrap" data-testid="folder-breadcrumb">
        <button data-testid="root-folder-button" onClick={goRoot} className={`flex items-center gap-1 font-bold ${atRoot ? "text-slate-900" : "text-blue-700 hover:text-blue-900"}`}>
          <FolderOpen size={18} className="text-amber-500" /> {t("mf.allVideos")}
        </button>
        {!atRoot && (
          <>
            <span className="text-slate-400">/</span>
            <span className="font-bold text-slate-900 truncate max-w-[260px]">{activeLib.name}{isCurrent ? t("mf.thisVideoSuffix") : ""}</span>
          </>
        )}
      </div>

      <div className="overflow-y-auto vb-scroll" style={{ maxHeight: 520 }} data-testid="broll-library">
        {loading ? (
          <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={28} /></div>
        ) : atRoot ? (
          /* ---- RAÍZ: una carpeta por video ---- */
          totallyEmpty ? (
            <Alert type="info">{t("mf.loadingEmptyRoot")}</Alert>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="video-folders">
              {videoFolders.map((vf) => (
                <VideoFolderTile key={vf.project_id} vf={vf} onOpen={() => openVideo(vf.project_id)}
                  onDelete={() => deleteWholeFolder(vf)} />
              ))}
            </div>
          )
        ) : openFolder ? (
          /* ---- DENTRO de una sub-carpeta (cortes) ---- */
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2 text-base">
                <button data-testid="folder-back-button" onClick={() => setOpenFolderId(null)} className="min-h-[44px] px-3 rounded-lg bg-slate-100 hover:bg-slate-200 font-bold flex items-center gap-1 text-slate-700">
                  <ArrowLeft size={18} /> {t("mf.back")}
                </button>
                <span className="font-bold text-slate-900 flex items-center gap-1"><FolderOpen size={18} className="text-amber-500" /> {openFolder.name}</span>
                <span className="text-sm text-slate-500">· {openFolder.parts.length} {t("mf.files")}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <AutofillButton testId={`use-folder-${openFolder.id}`} icon={Wand2}
                  onClick={() => onUse(openFolder.parts.map((p) => toClip(p)), openFolder.name, { fill: true })}
                  tip={t("mf.autofillTip")}
                  className="min-h-[44px] px-4 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-bold flex items-center gap-1">
                  {t("mf.autofillFolder")}
                </AutofillButton>
                <AutofillButton testId={`use-folder-all-${openFolder.id}`} icon={Layers}
                  onClick={() => onUse(openFolder.parts.map((p) => toClip(p)), openFolder.name, { fill: true, fillAll: true })}
                  tip={t("mf.autofillAllTip")}
                  className="min-h-[44px] px-4 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-sm font-bold flex items-center gap-1">
                  {t("mf.autofillFolderAll")}
                </AutofillButton>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {openFolder.parts.map((p) => (
                <ClipCard key={p.id} clip={toClip(p)}
                  primaryLabel={t("mf.addToScene")} primaryIcon={Plus}
                  primaryTestId={`use-autofill-${p.id}`}
                  onPrimary={() => onUse([toClip(p)], p.title)}
                  onDelete={isCurrent ? () => deleteItem(p.id, p.title) : undefined} deleteTestId={`delete-item-${p.id}`} />
              ))}
            </div>
          </div>
        ) : (
          /* ---- DENTRO de un video: sus carpetas (cortes) + archivos ---- */
          <div>
            {(activeLib.videos.length === 0 && activeLib.folders.length === 0) ? (
              <Alert type="info">{isCurrent ? t("mf.emptyCurrent") : t("mf.emptyOther")}</Alert>
            ) : (
              <>
                {targetLabel && (
                  <div className="flex flex-col sm:flex-row gap-2 mb-4">
                    <AutofillButton testId="autofill-all-button" icon={Wand2} iconSize={20}
                      onClick={() => onUse([
                        ...activeLib.videos.map((v) => toClip(v)),
                        ...activeLib.folders.flatMap((f) => f.parts.map((p) => toClip(p))),
                      ], isCurrent ? t("mf.thisVideo") : activeLib.name, { fill: true })}
                      tip={t("mf.autofillTip")}
                      className="flex-1 min-h-[48px] px-4 sm:px-5 text-base font-bold rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2">
                      {t("mf.autofillThisFolder")}
                    </AutofillButton>
                    <AutofillButton testId="autofill-all-video-button" icon={Layers} iconSize={20}
                      onClick={() => onUse([
                        ...activeLib.videos.map((v) => toClip(v)),
                        ...activeLib.folders.flatMap((f) => f.parts.map((p) => toClip(p))),
                      ], isCurrent ? t("mf.thisVideo") : activeLib.name, { fill: true, fillAll: true })}
                      tip={t("mf.autofillAllTip")}
                      className="flex-1 min-h-[48px] px-4 sm:px-5 text-base font-bold rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2">
                      {t("mf.autofillThisFolderAll")}
                    </AutofillButton>
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="folder-explorer">
                  {activeLib.folders.map((f) => (
                    <FolderTile key={f.id} folder={f}
                      onOpen={() => setOpenFolderId(f.id)}
                      onUse={() => onUse(f.parts.map((p) => toClip(p)), f.name, { fill: true })}
                      onUseAll={() => onUse(f.parts.map((p) => toClip(p)), f.name, { fill: true, fillAll: true })}
                      onDelete={isCurrent ? () => deleteItem(f.id, f.name) : undefined} />
                  ))}
                  {activeLib.videos.map((v) => (
                    <ClipCard key={v.id} clip={toClip(v)}
                      primaryLabel={t("mf.addToScene")} primaryIcon={Plus}
                      primaryTestId={`use-autofill-${v.id}`}
                      onPrimary={() => onUse([toClip(v)], v.title)}
                      canCut={isCurrent && v.can_cut && !replaceOnly} onCut={() => cutVideo(v.id, v.title)} cutting={isCutting(v.id)} cutProgress={getCut(v.id)}
                      onDelete={isCurrent ? () => deleteItem(v.id, v.title) : undefined} deleteTestId={`delete-item-${v.id}`} />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

    </Card>
  );
}

// Botón de autocompletar (escena o vídeo completo) con tooltip explicativo — el mismo patrón en
// los 3 sitios donde aparece (carpeta pequeña, carpeta abierta, carpeta del vídeo actual).
function AutofillButton({ tip, onClick, testId, className, icon: Icon, iconSize = 16, children }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button data-testid={testId} onClick={onClick} className={className}>
            <Icon size={iconSize} /> {children}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[240px] bg-slate-900 text-white text-sm font-medium leading-snug px-3 py-2 rounded-xl shadow-2xl border border-white/10">
          {tip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function FolderTile({ folder, onOpen, onUse, onUseAll, onDelete }) {
  const { t } = useTranslation();
  const count = folder.parts.length;
  const total = folder.parts.reduce((a, p) => a + (p.duration || 0), 0);
  const thumbs = folder.parts.slice(0, 4);
  return (
    <div data-testid={`folder-tile-${folder.id}`} className="border-2 border-amber-200 rounded-lg overflow-hidden group bg-amber-50/50 hover:border-amber-400 transition-colors">
      <button onClick={onOpen} className="block w-full text-left" data-testid={`open-folder-${folder.id}`}>
        <div className="relative w-full h-28 bg-amber-100 grid grid-cols-2 grid-rows-2 gap-0.5 p-0.5">
          {thumbs.map((p, i) => (
            <div key={i} className="bg-slate-300 overflow-hidden">
              {(p.thumbnail_url || p.thumb_path)
                ? <img src={p.thumbnail_url || `${process.env.REACT_APP_BACKEND_URL}/api/files/${p.thumb_path}`} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center bg-slate-700"><Film size={16} className="text-slate-400" /></div>}
            </div>
          ))}
          {Array.from({ length: Math.max(0, 4 - thumbs.length) }).map((_, i) => <div key={`e${i}`} className="bg-amber-100" />)}
          <div className="absolute inset-0 bg-amber-900/10 flex items-center justify-center">
            <span className="bg-white/90 rounded-lg px-2 py-1 flex items-center gap-1 text-amber-700 font-bold text-sm shadow"><Folder size={16} /> {t("mf.folder")}</span>
          </div>
        </div>
      </button>
      <div className="p-2">
        <div className="text-sm font-bold text-slate-900 truncate flex items-center gap-1" title={folder.name}><Folder size={14} className="text-amber-500 shrink-0" /> {folder.name}</div>
        <div className="text-xs text-slate-500 mt-0.5">{count} {t("mf.files")} · {fmtTime(total)}</div>
        <div className="flex items-center gap-1 mt-2">
          <AutofillButton testId={`use-folder-${folder.id}`} icon={Wand2} iconSize={14} onClick={onUse} tip={t("mf.autofillTip")}
            className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700 rounded-md py-2 text-sm font-bold flex items-center justify-center gap-1">
            {t("mf.autofillScene")}
          </AutofillButton>
          <AutofillButton testId={`use-folder-all-${folder.id}`} icon={Layers} iconSize={14} onClick={onUseAll} tip={t("mf.autofillAllTip")}
            className="flex-1 bg-indigo-600 text-white hover:bg-indigo-700 rounded-md py-2 text-sm font-bold flex items-center justify-center gap-1">
            {t("mf.autofillAllShort")}
          </AutofillButton>
          {onDelete && (
            <button data-testid={`delete-folder-${folder.id}`} onClick={onDelete} className="min-w-[36px] py-2 rounded-md bg-slate-200 hover:bg-red-100 text-slate-600 hover:text-red-600 flex items-center justify-center shrink-0"><Trash2 size={16} /></button>
          )}
        </div>
      </div>
    </div>
  );
}

function VideoFolderTile({ vf, onOpen, onDelete }) {
  const { t } = useTranslation();
  const parts = [...(vf.videos || []), ...((vf.folders || []).flatMap((f) => f.parts || []))];
  const count = parts.length;
  const thumbs = parts.slice(0, 4);
  return (
    <div data-testid={`video-folder-${vf.project_id}`} className="text-left border-2 border-amber-200 rounded-lg overflow-hidden group bg-amber-50/50 hover:border-amber-400 transition-colors">
      <button onClick={onOpen} className="block w-full text-left" data-testid={`open-video-folder-${vf.project_id}`}>
        <div className="relative w-full h-28 bg-amber-100 grid grid-cols-2 grid-rows-2 gap-0.5 p-0.5">
          {thumbs.map((p, i) => (
            <div key={i} className="bg-slate-300 overflow-hidden">
              {(p.thumbnail_url || p.thumb_path)
                ? <img src={p.thumbnail_url || `${process.env.REACT_APP_BACKEND_URL}/api/files/${p.thumb_path}`} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center bg-slate-700"><Film size={16} className="text-slate-400" /></div>}
            </div>
          ))}
          {Array.from({ length: Math.max(0, 4 - thumbs.length) }).map((_, i) => <div key={`e${i}`} className="bg-amber-100" />)}
          <div className="absolute inset-0 bg-amber-900/10 flex items-center justify-center">
            <span className="bg-white/90 rounded-lg px-2 py-1 flex items-center gap-1 text-amber-700 font-bold text-sm shadow"><Folder size={16} /> {vf.is_current ? t("mf.thisVideo") : t("mf.open")}</span>
          </div>
        </div>
      </button>
      <div className="p-2">
        <div className="text-sm font-bold text-slate-900 truncate flex items-center gap-1" title={vf.name}><Folder size={14} className="text-amber-500 shrink-0" /> {vf.name}</div>
        <div className="flex items-center justify-between gap-1 mt-0.5">
          <span className="text-xs text-slate-500">{t("mf.filesCount", { count })}{vf.is_current ? t("mf.thisVideoDot") : ""}</span>
          {count > 0 && (
            <button data-testid={`delete-video-folder-${vf.project_id}`}
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              title={t("mf.removeFolderTip")}
              className="shrink-0 rounded-md p-1 text-slate-400 hover:bg-red-100 hover:text-red-600"><Trash2 size={14} /></button>
          )}
        </div>
      </div>
    </div>
  );
}
